import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, useAnimatedClose } from './Dialog';

/**
 * Profilkep-vago.
 *
 * A megjelenites negyzetes marad, de a vagast a FELHASZNALO donti el. Eddig a
 * szerver vagott automatikusan ("figyelem-alapu" pozicionalassal), es az egy
 * allo kepnel rendszeresen a rossz reszt tartotta meg - egy csoportkepbol a
 * hattert, egy egesz alakos kepbol a labat.
 *
 * ─── Amit a felhasznalo lat, azt kapja ───────────────────────────────────────
 *
 * A vagas ITT tortenik, nem a szerveren: a kivagott negyzetet toltjuk fel. Igy
 * nincs mod arra, hogy a kep mast mutasson, mint az elonezet - koordinatakat
 * kuldve mindig marad egy kerekitesi elteres a ketto kozott.
 */

/** A kimeneti kep merete. Ugyanaz, amit a szerver is hasznal. */
const OUTPUT_SIZE = 512;

/** A vago negyzet merete a kepernyon. */
const VIEW = 288;

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;

export function AvatarCropper({
  file,
  onCancel: cancelNow,
  onDone,
}: {
  file: File;
  onCancel: () => void;
  onDone: (cropped: File) => void;
}) {
  const onCancel = useAnimatedClose(cancelNow);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [error, setError] = useState<string | null>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);

  // ─── A kep betoltese ────────────────────────────────────────────────────────

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setImage(img);
    img.onerror = () => setError('Ezt a képet nem sikerült megnyitni.');
    img.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  /**
   * A kep alapmerete a nezetben.
   *
   * A RÖVIDEBB oldal toltse ki a negyzetet - igy a kezdoallapotban sosem
   * latszik ures sav, es a felhasznalo csak nagyitani tud, kicsinyiteni nem
   * kell.
   */
  const base = image ? VIEW / Math.min(image.width, image.height) : 1;

  /** Meddig lehet huzni: a kep ne csusszon el a negyzet alol. */
  const clamp = useCallback(
    (next: { x: number; y: number }, atZoom: number) => {
      if (!image) return { x: 0, y: 0 };
      const w = image.width * base * atZoom;
      const h = image.height * base * atZoom;
      const maxX = Math.max(0, (w - VIEW) / 2);
      const maxY = Math.max(0, (h - VIEW) / 2);
      return {
        x: Math.min(maxX, Math.max(-maxX, next.x)),
        y: Math.min(maxY, Math.max(-maxY, next.y)),
      };
    },
    [image, base],
  );

  // A nagyitas kozeppontbol tortenik, ezert az eltolast is ujra kell hatarolni.
  const changeZoom = (next: number) => {
    const value = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    setZoom(value);
    setOffset((current) => clamp(current, value));
  };

  // ─── Vagas ──────────────────────────────────────────────────────────────────

  const apply = async () => {
    if (!image) return;

    const canvas = document.createElement('canvas');
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('A böngésző nem tudta elvégezni a vágást.');
      return;
    }

    /*
      A nezetbeli meretekbol szamolunk vissza a kep sajat keppontjaira.

      A `scale` a nezet es a kimenet aranya: amit a felhasznalo a 288 pontos
      negyzetben lat, azt kell 512 ponton kirajzolni.
    */
    const scale = OUTPUT_SIZE / VIEW;
    const w = image.width * base * zoom * scale;
    const h = image.height * base * zoom * scale;
    const x = (OUTPUT_SIZE - w) / 2 + offset.x * scale;
    const y = (OUTPUT_SIZE - h) / 2 + offset.y * scale;

    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, x, y, w, h);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/webp', 0.9),
    );
    if (!blob) {
      setError('A vágott kép elkészítése nem sikerült.');
      return;
    }

    onDone(new File([blob], 'profilkep.webp', { type: 'image/webp' }));
  };

  // ─── Huzas ──────────────────────────────────────────────────────────────────

  const onPointerDown = (event: React.PointerEvent) => {
    drag.current = { x: event.clientX - offset.x, y: event.clientY - offset.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const start = drag.current;
    if (!start) return;
    setOffset(clamp({ x: event.clientX - start.x, y: event.clientY - start.y }, zoom));
  };

  const onPointerUp = () => {
    drag.current = null;
  };

  /*
    BILLENTYUZETTEL is mozgathato: a huzas egymagaban kizarna azt, aki nem
    egeret hasznal. A nyil a kepet abba az iranyba tolja, mint a huzas;
    Shifttel nagyobbat lep. Ugyanaz a hatarolas vonatkozik ra.
  */
  const onKeyDown = (event: React.KeyboardEvent) => {
    const step = event.shiftKey ? 40 : 10;
    const delta: Record<string, { x: number; y: number }> = {
      ArrowLeft: { x: -step, y: 0 },
      ArrowRight: { x: step, y: 0 },
      ArrowUp: { x: 0, y: -step },
      ArrowDown: { x: 0, y: step },
    };
    const move = delta[event.key];
    if (!move) return;
    event.preventDefault();
    setOffset((current) => clamp({ x: current.x + move.x, y: current.y + move.y }, zoom));
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => !next && onCancel()}
      title="Profilkép beállítása"
      description="Húzd a képet a helyére, és nagyíts, ha kell."
    >
      {error ? (
        <p
          role="alert"
          className="rounded-control border border-danger/30 bg-danger-soft px-3 py-2.5 text-sm text-danger"
        >
          {error}
        </p>
      ) : (
        <div className="flex flex-col items-center gap-4">
          {/*
            A KEREK MASZK csak elonezet.

            A tarolt kep negyzetes marad - a felulet rajzol kort belole. Igy a
            felhasznalo azt latja, amit a tobbiek is latni fognak.
          */}
          <div
            role="img"
            aria-label="A kép kivágása. A nyílbillentyűkkel mozgathatod, Shifttel nagyobbat lép."
            tabIndex={0}
            className="relative cursor-grab overflow-hidden rounded-full bg-surface-sunken outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-raised active:cursor-grabbing"
            style={{ width: VIEW, height: VIEW, touchAction: 'none' }}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {image && (
              <img
                src={image.src}
                alt=""
                draggable={false}
                className="pointer-events-none absolute left-1/2 top-1/2 max-w-none"
                style={{
                  width: image.width * base * zoom,
                  height: image.height * base * zoom,
                  transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                }}
              />
            )}
          </div>

          <label className="flex w-full max-w-xs items-center gap-3">
            <span className="text-2xs text-ink-faint">Nagyítás</span>
            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.01}
              value={zoom}
              onChange={(event) => changeZoom(Number(event.target.value))}
              className="flex-1 accent-[var(--accent)]"
            />
          </label>
        </div>
      )}

      <div className="mt-5 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-control px-3 py-2 text-sm text-ink-muted hover:text-ink"
        >
          Mégse
        </button>
        <button
          type="button"
          onClick={() => void apply()}
          disabled={!image || Boolean(error)}
          className="rounded-control bg-accent px-4 py-2 text-sm font-semibold text-accent-ink disabled:opacity-50"
        >
          Beállítom
        </button>
      </div>
    </Dialog>
  );
}
